"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function TalentWebsitePipeline({ section }) {
  return (
    <section className="py-20 relative overflow-hidden bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Header Block */}
        <div className="grid lg:grid-cols-12 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-10 lg:col-start-2 space-y-6"
          >
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}>
              From your{" "}
              <span className="relative inline-block mr-2">
                <span className="relative z-10 text-white">website</span>
                {/* Blue highlighter stroke */}
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#38bdf8] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>{" "}
              to your{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-white">pipeline</span>
                {/* Yellow highlighter stroke */}
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#fbbf24] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
          </motion.div>

          {/* Paragraph */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-8 lg:col-start-2"
          >
            <p className="text-gray-300 leading-relaxed font-normal text-xl">
              <strong className="text-white font-bold">Smart contact form.</strong> When filled out on your website, it automatically creates an opportunity in your CRM with the right information and the jobs your visitor is interested in.
            </p>
          </motion.div>
        </div>

        {/* Visual Mockups Row */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-10 max-w-[1200px] mx-auto mt-16 relative">

          {/* Background circle decorative element */}
          <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[120px] pointer-events-none -z-10" />

          {/* Left Screen: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[42%] flex justify-center"
          >
            <Image
              src="/odoo/industries/talent-acquisition/talent-acq-contact-us.webp"
              width={500}
              height={350}
              className="w-full h-auto rounded-xl object-contain shadow-2xl"
              alt="Talent Acquisition Contact Form"
            />
          </motion.div>

          {/* Middle: Rotated/Flipped Arrow Doodle */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-20 h-20 flex items-center justify-center select-none pointer-events-none z-20 d-none d-lg-flex"
          >
            {/* arrow_md_01.svg, rotated and flipped */}
            <Image
              src="/odoo/arrows/arrow_md_01.svg"
              width={80}
              height={80}
              className="w-full h-auto brightness-0 invert opacity-45 transform rotate-[90deg] scale-y-[-1]"
              alt=""
            />
          </motion.div>

          {/* Right Screen: CRM Pipeline */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="w-[80%] sm:w-[65%] md:w-[55%] lg:w-[32%] flex justify-center relative group"
          >
            {/* Orange Hand-drawn Corner Spark/Burst Doodle matching theme color */}
            <div className="absolute -top-8 -right-8 w-20 h-20 pointer-events-none select-none z-20 opacity-90 group-hover:scale-110 transition-transform duration-300">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#f1833b]">
                {/* Ray 1: pointing top-left */}
                <path d="M 30 70 C 24 55, 14 42, 2 34" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                {/* Ray 2: pointing top-right */}
                <path d="M 40 65 C 44 48, 48 30, 52 12" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                {/* Ray 3: pointing middle-right */}
                <path d="M 48 70 C 62 66, 78 62, 94 58" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
                {/* Ray 4: pointing bottom-right */}
                <path d="M 42 78 C 52 86, 64 94, 76 99" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              </svg>
            </div>

            <Image
              src="/odoo/industries/talent-acquisition/talent-acq-crm.webp"
              width={400}
              height={550}
              className="w-full h-auto rounded-xl object-contain shadow-2xl"
              alt="Odoo CRM Talent Opportunity"
            />
          </motion.div>

        </div>

      </div>
    </section>
  );
}

export function TalentPoolManagement({ section }) {
  return (
    <section className="py-16 relative overflow-hidden bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">


        {/* Heading Block */}
        <div className="text-center max-w-[1000px] mx-auto mb-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}
          >
            Efficiently manage your{" "}
            <span className="relative inline-block px-6 ml-2">
              <span className="relative z-10 text-white">talent pool</span>
              {/* Orange Hand-drawn Marker Highlighter background */}
              <svg className="absolute inset-0 w-full h-[120%] -top-[10%] -z-10 scale-y-110 origin-center opacity-85" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path
                  d="M 5 25 C 30 18, 70 12, 95 15 C 98 25, 96 45, 95 75 C 70 78, 30 82, 5 78 C 2 60, 3 40, 5 25 Z"
                  fill="#f1833b"
                />
              </svg>
              {/* Crown absolute centered above in theme orange color */}
              <div className="absolute bottom-[95%] left-1/2 -translate-x-1/2 w-16 h-12 rotate-[10deg] d-none d-lg-block animate-bounce-slow">
                <svg viewBox="0 0 100 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#f1833b]">
                  {/* 3-point crown shape fill */}
                  <path
                    d="M 18 58 C 24 45, 12 25, 20 20 C 26 35, 45 42, 50 15 C 55 42, 74 35, 80 20 C 88 25, 76 45, 82 58 Z"
                    fill="currentColor"
                  />
                  {/* Crown dark hand-drawn border line */}
                  <path
                    d="M 18 58 Q 50 64 82 58 L 80 20 C 74 35, 55 42, 50 15 C 45 42, 26 35, 20 20 Z"
                    stroke="#111"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Sparkle dashes on top */}
                  <path d="M 20 10 L 15 2" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M 38 8 L 36 0" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M 50 5 L 50 0" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M 62 8 L 64 0" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M 80 10 L 85 2" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                </svg>
              </div>
            </span>
          </motion.h2>
        </div>

        {/* Subtext Paragraph */}
        <div className="text-center max-w-[800px] mx-auto mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-300 leading-relaxed font-normal text-xl"
          >
            <strong className="text-white font-bold">Track all the positions a candidate has applied for.</strong> Any updates to their information and skills are automatically reflected across all applications. Give every candidate their best opportunity!
          </motion.p>
        </div>

        {/* Large Mockup Image Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative w-full max-w-[1150px] mx-auto group select-none"
        >
          {/* Subtle backdrop circle without border */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] sm:w-[500px] lg:w-[700px] h-[340px] sm:h-[500px] lg:h-[700px] bg-white/[0.02] rounded-full -z-10 pointer-events-none" />

          {/* Top-Left Sparkle Doodle in orange brand theme color, set perfectly on the corner and pointing strictly outwards */}
          <div className="absolute -top-20 -left-20 w-24 h-24 pointer-events-none select-none z-20 opacity-100 group-hover:scale-110 transition-transform duration-300">
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#f1833b]">
              {/* Ray 1: pointing left-down (away from mockup) */}
              <path d="M 75 75 C 60 78, 42 80, 20 82" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              {/* Ray 2: pointing top-left (away from mockup) */}
              <path d="M 70 70 C 55 58, 38 42, 22 26" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              {/* Ray 3: pointing straight up (away from mockup) */}
              <path d="M 75 65 C 72 45, 68 28, 62 8" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
              {/* Ray 4: pointing middle-left (away from mockup) */}
              <path d="M 70 75 C 52 70, 35 65, 12 60" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" />
            </svg>
          </div>

          {/* Clean image mockup, restored to original bounds to fix button overlaps */}
          <div className="relative w-full rounded-2xl bg-transparent p-0">
            <Image
              src="/odoo/industries/talent-acquisition/efficiently-manage.webp"
              width={1150}
              height={750}
              className="w-full h-auto rounded-xl object-contain shadow-2xl"
              alt="Efficiently Manage Talent Pool"
            />
          </div>
        </motion.div>

      </div>
    </section>
  );
}

export function TalentTailorMadePipeline({ section }) {
  return (
    <section className="pb-5 pb-lg-7 bg-transparent w-full font-sans text-white relative z-10">
      <div className="container mx-auto px-6 max-w-[1400px]">
        {/* Grey Arrow Loop Dot above the row */}
        <Image
          src="/odoo/arrows/grey_arrow_dot_02.svg"
          alt=""
          className="rotate-300 mx-auto d-none d-lg-block brightness-0 invert opacity-45"
          width={100}
          height={100}
        />

        <div className="row g-5 align-items-end align-items-center flex flex-col lg:flex-row gap-8 lg:gap-0 mt-8">
          {/* Left Column: Image in the shape wrapper */}
          <div className="col-lg-5 x_wd_shape x_wd_shape_re x_wd_shape_or py-5 w-full lg:w-[42%] relative flex justify-center">
            {/* White/Grey Dome Shape Backdrop Bubble */}
            <div className="absolute top-0 bottom-0 left-[-15%] right-[10%] bg-white/[0.03] rounded-r-[180px] -z-10" />

            <Image
              src="/odoo/industries/talent-acquisition/you-re-hired.webp"
              className="img-thumbnail w-full lg:w-100 ms-auto rounded-2xl border border-white/10 shadow-2xl object-contain"
              alt="You're hired"
              width={600}
              height={400}
            />
          </div>

          {/* Right Column: Trophy, Heading and Description */}
          <div className="col-lg-6 offset-lg-1 w-full lg:w-[50%] lg:ml-[8%] space-y-6">
            <Image
              src="/odoo/icons/yellow_trophy.svg"
              width={100}
              height={70}
              className="d-none d-lg-inline ms-6 mb-4 rotate-350"
              alt="Trophy"
              style={{ filter: "hue-rotate(-20deg) saturate(1.5)" }}
            />

            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}>
              Tailor-made pipeline to fit your{" "}
              <span className="relative inline-block ml-1">
                <span className="relative z-10 text-white">workflow</span>
                {/* Brand Orange Highlighter Stroke */}
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8" stroke="currentColor" strokeWidth="4.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h2>

            <p className="text-gray-300 leading-relaxed font-normal text-xl">
              <strong className="text-white font-bold">Candidates progress through the stages automatically:</strong> from initial opportunities to service agreement signatures and from recruitment to finalized contracts.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TalentNeverLoseTrack({ section }) {
  return (
    <section className="pb-16 pt-8 text-center bg-transparent w-full font-sans text-white relative z-10">
      <div className="container mx-auto px-6 max-w-[1200px]">
        {/* Title */}
        <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold mb-4 text-white ${caveat.className}`}>
          {section.title}
        </h2>
        {/* Subtext */}
        <p className="text-gray-300 mb-12 max-w-2xl mx-auto font-normal text-xl leading-relaxed">
          {section.subtext}
        </p>

        {/* Rounded Glassmorphic Icon Grid container */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 bg-white/[0.03] border border-white/10 rounded-2xl p-10 lg:p-14 text-center shadow-2xl relative overflow-hidden backdrop-blur-sm">
          {/* Subtle internal decorative background glow */}
          <div className="absolute inset-0 bg-[#f1833b]/5 rounded-full blur-[100px] pointer-events-none -z-10" />

          {section.items?.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex flex-col items-center group"
            >
              <div className="w-20 h-20 flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/5 mb-4 group-hover:scale-110 group-hover:bg-white/[0.08] group-hover:border-white/20 transition-all duration-300 shadow-md">
                {item.icon && item.icon.trim() !== "" ? (
                  <Image
                    src={item.icon}
                    className="w-12 h-12 object-contain"
                    alt={item.label}
                    width={48}
                    height={48}
                  />
                ) : (
                  <span className="text-2xl">✨</span>
                )}
              </div>
              <span className="text-xl md:text-2xl font-bold text-white group-hover:text-[#f1833b] transition-colors duration-300">
                {item.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
